// src/loggerInstance.ts
var LoggerInstance = class {
  constructor(name, storage, withTimestamps = true, consoleLogging = false, enabled = true) {
    this.name = name;
    this.storage = storage;
    this.withTimestamps = withTimestamps;
    this.consoleLogging = consoleLogging;
    this.enabled = enabled;
  }
  writeLog(...args) {
    if (!this.enabled) {
      if (this.consoleLogging) console.log(`[${this.name}]`, ...args);
      return;
    }
    const message = args.map(
      (a) => typeof a === "string" ? a : a instanceof Error ? `${a.message}
${a.stack}` : JSON.stringify(a)
    ).join(" ");
    const entry = {
      name: this.name,
      message,
      timestamp: this.withTimestamps ? (/* @__PURE__ */ new Date()).toISOString() : void 0
    };
    this.storage.append(entry).catch((error) => {
      console.error("Failed to write log to storage:", error);
    });
    if (this.consoleLogging) console.log(`[${this.name}]`, ...args);
  }
  setConsoleLogging(enabled) {
    this.consoleLogging = enabled;
  }
  setTimestamps(enabled) {
    this.withTimestamps = enabled;
  }
  setEnabled(enabled) {
    this.enabled = enabled;
  }
};

// src/indexedDBAdapter.ts
var IndexedDBAdapter = class {
  constructor(dbName = "__illogger_db__", storeName = "logs", version = 1) {
    this.db = null;
    this.dbName = dbName;
    this.storeName = storeName;
    this.version = version;
  }
  /**
   * Initialize the IndexedDB database
   * Returns a promise that resolves when the database is ready
   */
  async init() {
    if (this.db) {
      return this.db;
    }
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      request.onerror = () => {
        reject(new Error(`Failed to open IndexedDB: ${request.error}`));
      };
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: "id",
            autoIncrement: true
          });
          objectStore.createIndex("timestamp", "timestamp", { unique: false });
          objectStore.createIndex("name", "name", { unique: false });
        }
      };
    });
  }
  /**
   * Read all logs from IndexedDB
   */
  async read() {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], "readonly");
        const store = transaction.objectStore(this.storeName);
        const request = store.getAll();
        request.onerror = () => {
          reject(new Error(`Failed to read from IndexedDB: ${request.error}`));
        };
        request.onsuccess = () => {
          resolve(request.result || []);
        };
      });
    } catch (error) {
      console.error("IndexedDB read error:", error);
      return [];
    }
  }
  /**
   * Write logs to IndexedDB
   * Replaces all existing logs with the new array
   */
  async write(logs) {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], "readwrite");
        const store = transaction.objectStore(this.storeName);
        const clearRequest = store.clear();
        clearRequest.onerror = () => {
          reject(
            new Error(
              `Failed to clear IndexedDB: ${clearRequest.error}`
            )
          );
        };
        clearRequest.onsuccess = () => {
          if (logs.length === 0) {
            resolve();
            return;
          }
          let completed = 0;
          let hasError = false;
          logs.forEach((log) => {
            const addRequest = store.add(log);
            addRequest.onerror = () => {
              if (!hasError) {
                hasError = true;
                reject(
                  new Error(
                    `Failed to write to IndexedDB: ${addRequest.error}`
                  )
                );
              }
            };
            addRequest.onsuccess = () => {
              completed++;
              if (completed === logs.length && !hasError) {
                resolve();
              }
            };
          });
        };
      });
    } catch (error) {
      console.error("IndexedDB write error:", error);
      throw error;
    }
  }
  /**
   * Append a single log entry to IndexedDB
   */
  async append(entry) {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], "readwrite");
        const store = transaction.objectStore(this.storeName);
        const request = store.add(entry);
        request.onerror = () => {
          reject(new Error(`Failed to append to IndexedDB: ${request.error}`));
        };
        request.onsuccess = () => {
          resolve();
        };
      });
    } catch (error) {
      console.error("IndexedDB append error:", error);
      throw error;
    }
  }
  /**
   * Clear all logs from IndexedDB
   */
  async clear() {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], "readwrite");
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();
        request.onerror = () => {
          reject(new Error(`Failed to clear IndexedDB: ${request.error}`));
        };
        request.onsuccess = () => {
          resolve();
        };
      });
    } catch (error) {
      console.error("IndexedDB clear error:", error);
      throw error;
    }
  }
  /**
   * Get count of logs in IndexedDB
   */
  async count() {
    try {
      const db = await this.init();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], "readonly");
        const store = transaction.objectStore(this.storeName);
        const request = store.count();
        request.onerror = () => {
          reject(new Error(`Failed to count IndexedDB: ${request.error}`));
        };
        request.onsuccess = () => {
          resolve(request.result);
        };
      });
    } catch (error) {
      console.error("IndexedDB count error:", error);
      return 0;
    }
  }
  /**
   * Close the database connection
   */
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
};

// src/storageAdapter.ts
var StorageAdapter = class {
  constructor(key = "__illogger__", maxEntries = 5e3) {
    this.key = key;
    this.pendingWrites = [];
    this.writeTimer = null;
    this.maxEntries = maxEntries;
    this.db = new IndexedDBAdapter(key, "logs", 1);
  }
  getMaxLogs() {
    return this.maxEntries;
  }
  async setMaxLogs(maxLogs) {
    if (maxLogs < 1) {
      throw new Error("maxLogs must be at least 1");
    }
    await this.flushPendingWrites();
    this.maxEntries = maxLogs;
    const currentCount = await this.db.count();
    if (currentCount > this.maxEntries) {
      await this.trimOldEntries();
    }
  }
  /**
   * Append a log entry to IndexedDB
   * Uses batching to improve performance for high-frequency logging
   */
  async append(entry) {
    this.pendingWrites.push(entry);
    if (this.writeTimer) {
      clearTimeout(this.writeTimer);
    }
    this.writeTimer = setTimeout(async () => {
      await this.flushPendingWrites();
    }, 100);
  }
  /**
   * Flush pending writes to IndexedDB
   */
  async flushPendingWrites() {
    if (this.pendingWrites.length === 0) return;
    const toWrite = [...this.pendingWrites];
    this.pendingWrites = [];
    try {
      const currentCount = await this.db.count();
      const willExceedLimit = currentCount + toWrite.length > this.maxEntries;
      if (willExceedLimit) {
        await this.trimOldEntries(currentCount + toWrite.length - this.maxEntries);
      }
      await Promise.all(toWrite.map((entry) => this.db.append(entry)));
      const finalCount = await this.db.count();
      if (finalCount > this.maxEntries) {
        await this.trimOldEntries();
      }
    } catch (error) {
      console.error("Failed to flush pending writes:", error);
      this.pendingWrites.unshift(...toWrite);
    }
  }
  /**
   * Trim old entries to maintain maxEntries limit
   * @param excessCount - Optional number of excess entries to remove (defaults to removing all excess)
   */
  async trimOldEntries(excessCount) {
    try {
      const logs = await this.db.read();
      if (excessCount === void 0 && logs.length <= this.maxEntries) {
        return;
      }
      const sorted = logs.sort((a, b) => (a.id || 0) - (b.id || 0));
      let toKeep;
      if (excessCount !== void 0 && excessCount > 0) {
        toKeep = sorted.slice(excessCount);
      } else {
        toKeep = sorted.slice(-this.maxEntries);
      }
      const logsWithoutId = toKeep.map(({ id, ...log }) => log);
      await this.db.write(logsWithoutId);
    } catch (error) {
      console.error("Failed to trim old entries:", error);
    }
  }
  /**
   * Get all logs from IndexedDB
   */
  async getAll() {
    await this.flushPendingWrites();
    const logs = await this.db.read();
    return logs.map(({ id, ...log }) => log);
  }
  /**
   * Get count of logs in storage
   */
  async count() {
    await this.flushPendingWrites();
    return await this.db.count();
  }
  /**
   * Clear all logs from IndexedDB
   */
  async clear() {
    this.pendingWrites = [];
    if (this.writeTimer) {
      clearTimeout(this.writeTimer);
      this.writeTimer = null;
    }
    await this.db.clear();
  }
  /**
   * Close the database connection
   */
  close() {
    if (this.writeTimer) {
      clearTimeout(this.writeTimer);
      this.writeTimer = null;
    }
    this.flushPendingWrites().finally(() => {
      this.db.close();
    });
  }
};

// src/uiButton.ts
import { saveAs } from "file-saver";
import JSZip from "jszip";
async function downloadLogs(storage, singleFile, showTimestamps) {
  const logs = await storage.getAll();
  const shouldShowTimestamps = typeof showTimestamps === "function" ? showTimestamps() : showTimestamps;
  if (singleFile) {
    const content = logs.map((log) => {
      if (log.isSeparator || log.name === "__session_separator__") {
        const timestamp2 = shouldShowTimestamps && log.timestamp ? `[${log.timestamp}] ` : "";
        const separatorLine = "=".repeat(60);
        return `
${separatorLine}
${timestamp2}${log.message}
${separatorLine}
`;
      }
      const loggerPrefix = log.name ? `[${log.name}] ` : "";
      const timestamp = shouldShowTimestamps && log.timestamp ? `[${log.timestamp}] ` : "";
      return `${timestamp}${loggerPrefix}${log.message}`;
    }).join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    saveAs(blob, "illogger-logs.log");
  } else {
    const grouped = {};
    logs.forEach((log) => {
      var _a;
      if (log.isSeparator || log.name === "__session_separator__") {
        Object.keys(grouped).forEach((key) => {
          grouped[key].push(log);
        });
      } else {
        (grouped[_a = log.name] || (grouped[_a] = [])).push(log);
      }
    });
    const zip = new JSZip();
    Object.entries(grouped).forEach(([name, entries]) => {
      const content = entries.map((e) => {
        if (e.isSeparator || e.name === "__session_separator__") {
          const timestamp2 = shouldShowTimestamps && e.timestamp ? `[${e.timestamp}] ` : "";
          const separatorLine = "=".repeat(60);
          return `
${separatorLine}
${timestamp2}${e.message}
${separatorLine}
`;
        }
        const timestamp = shouldShowTimestamps && e.timestamp ? `[${e.timestamp}] ` : "";
        return `${timestamp}${e.message}`;
      }).join("\n");
      zip.file(`${name}.log`, content);
    });
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, "illogger-logs.zip");
  }
}
var POSITION_STORAGE_KEY = "illogger-button-position";
function getSavedPosition() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return null;
  }
  try {
    const saved = localStorage.getItem(POSITION_STORAGE_KEY);
    if (saved) {
      const { top, left } = JSON.parse(saved);
      return { top, left };
    }
  } catch (e) {
  }
  return null;
}
function savePosition(top, left) {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ top, left }));
  } catch (e) {
  }
}
function injectDownloadButton(storage, singleFile = false, showTimestamps = true, buttonOptions) {
  var _a;
  if (typeof document === "undefined") return;
  if (document.getElementById("illogger-download-btn")) return;
  const getShowTimestamps = () => typeof showTimestamps === "function" ? showTimestamps() : showTimestamps;
  const btn = document.createElement("button");
  btn.id = "illogger-download-btn";
  btn.textContent = (_a = buttonOptions == null ? void 0 : buttonOptions.text) != null ? _a : "Interactive Logger";
  btn.draggable = false;
  const savedPosition = getSavedPosition();
  const defaultTop = window.innerHeight - 60;
  const defaultLeft = window.innerWidth - 100;
  const defaultStyles = {
    position: "fixed",
    top: savedPosition ? `${savedPosition.top}px` : `${defaultTop}px`,
    left: savedPosition ? `${savedPosition.left}px` : `${defaultLeft}px`,
    background: "#111",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: "8px",
    fontSize: "14px",
    border: "none",
    cursor: "grab",
    zIndex: "99999",
    opacity: "0.8",
    width: "80px",
    height: "40px",
    userSelect: "none",
    touchAction: "none",
    // Prevent touch scrolling on mobile
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none"
  };
  const mergedStyles = { ...defaultStyles, ...(buttonOptions == null ? void 0 : buttonOptions.style) || {} };
  Object.assign(btn.style, mergedStyles);
  btn.onmouseenter = () => {
    if (!btn.dataset.dragging) {
      btn.style.opacity = "1";
    }
  };
  btn.onmouseleave = () => {
    if (!btn.dataset.dragging) {
      btn.style.opacity = "0.8";
    }
  };
  let isDragging = false;
  let hasDragged = false;
  let startX = 0;
  let startY = 0;
  let startLeft = 0;
  let startTop = 0;
  let isUsingTouch = false;
  const startDrag = (clientX, clientY) => {
    isDragging = true;
    hasDragged = false;
    btn.dataset.dragging = "true";
    btn.style.cursor = "grabbing";
    btn.style.opacity = "1";
    startX = clientX;
    startY = clientY;
    const rect = btn.getBoundingClientRect();
    startLeft = rect.left;
    startTop = rect.top;
  };
  const updateDrag = (clientX, clientY) => {
    if (!isDragging) return;
    if (!hasDragged) {
      const deltaX2 = Math.abs(clientX - startX);
      const deltaY2 = Math.abs(clientY - startY);
      if (deltaX2 > 5 || deltaY2 > 5) {
        hasDragged = true;
      }
    }
    const deltaX = clientX - startX;
    const deltaY = clientY - startY;
    let newLeft = startLeft + deltaX;
    let newTop = startTop + deltaY;
    const btnRect = btn.getBoundingClientRect();
    const maxLeft = window.innerWidth - btnRect.width;
    const maxTop = window.innerHeight - btnRect.height;
    newLeft = Math.max(0, Math.min(newLeft, maxLeft));
    newTop = Math.max(0, Math.min(newTop, maxTop));
    btn.style.left = `${newLeft}px`;
    btn.style.top = `${newTop}px`;
  };
  const endDrag = () => {
    if (isDragging) {
      const wasDragging = hasDragged;
      isDragging = false;
      delete btn.dataset.dragging;
      btn.style.cursor = "grab";
      btn.style.opacity = "0.8";
      const rect = btn.getBoundingClientRect();
      savePosition(rect.top, rect.left);
      if (wasDragging) {
        setTimeout(() => {
          hasDragged = false;
        }, 100);
      }
    }
    isUsingTouch = false;
  };
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    if (isUsingTouch) return;
    startDrag(e.clientX, e.clientY);
    e.preventDefault();
    e.stopPropagation();
  };
  const handleMouseMove = (e) => {
    if (isUsingTouch) return;
    updateDrag(e.clientX, e.clientY);
  };
  const handleMouseUp = (e) => {
    if (isUsingTouch) return;
    endDrag();
  };
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    isUsingTouch = true;
    const touch = e.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    const rect = btn.getBoundingClientRect();
    startLeft = rect.left;
    startTop = rect.top;
    hasDragged = false;
    isDragging = false;
    e.preventDefault();
    e.stopPropagation();
  };
  const handleTouchMove = (e) => {
    if (!isUsingTouch) return;
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    if (!isDragging) {
      const deltaX = Math.abs(touch.clientX - startX);
      const deltaY = Math.abs(touch.clientY - startY);
      if (deltaX > 5 || deltaY > 5) {
        isDragging = true;
        hasDragged = true;
        btn.dataset.dragging = "true";
        btn.style.cursor = "grabbing";
        btn.style.opacity = "1";
      } else {
        return;
      }
    }
    updateDrag(touch.clientX, touch.clientY);
    e.preventDefault();
    e.stopPropagation();
  };
  const handleTouchEnd = (e) => {
    if (!isUsingTouch) return;
    if (isDragging) {
      endDrag();
      e.preventDefault();
      e.stopPropagation();
    } else {
      isUsingTouch = false;
      hasDragged = false;
      e.preventDefault();
      e.stopPropagation();
      setTimeout(async () => {
        await downloadLogs(storage, singleFile, getShowTimestamps);
      }, 0);
    }
  };
  const handleTouchCancel = (e) => {
    if (!isUsingTouch) return;
    if (isDragging) {
      endDrag();
    } else {
      isUsingTouch = false;
      hasDragged = false;
    }
    e.preventDefault();
    e.stopPropagation();
  };
  document.body.appendChild(btn);
  btn.addEventListener("mousedown", handleMouseDown);
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
  btn.addEventListener("touchstart", handleTouchStart, { passive: false });
  document.addEventListener("touchmove", handleTouchMove, { passive: false });
  document.addEventListener("touchend", handleTouchEnd, { passive: false });
  document.addEventListener("touchcancel", handleTouchCancel, { passive: false });
  const onWindowResize = () => {
    const rect = btn.getBoundingClientRect();
    const maxLeft = window.innerWidth - rect.width;
    const maxTop = window.innerHeight - rect.height;
    let currentLeft = rect.left;
    let currentTop = rect.top;
    if (currentLeft > maxLeft) {
      currentLeft = maxLeft;
    }
    if (currentTop > maxTop) {
      currentTop = maxTop;
    }
    if (currentLeft < 0) {
      currentLeft = 0;
    }
    if (currentTop < 0) {
      currentTop = 0;
    }
    btn.style.left = `${currentLeft}px`;
    btn.style.top = `${currentTop}px`;
    savePosition(currentTop, currentLeft);
  };
  window.addEventListener("resize", onWindowResize);
  const handleClick = async (e) => {
    if (hasDragged || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    await downloadLogs(storage, singleFile, getShowTimestamps);
  };
  btn.addEventListener("click", handleClick);
  btn.__illogger_cleanup = () => {
    btn.removeEventListener("mousedown", handleMouseDown);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    btn.removeEventListener("touchstart", handleTouchStart);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
    document.removeEventListener("touchcancel", handleTouchCancel);
    window.removeEventListener("resize", onWindowResize);
    btn.removeEventListener("click", handleClick);
  };
}
function withdrawDownloadButton() {
  if (typeof document === "undefined") return;
  const btn = document.getElementById("illogger-download-btn");
  if (!btn) return;
  if (btn.__illogger_cleanup) {
    btn.__illogger_cleanup();
  }
  btn.remove();
}

// src/interactiveLogger.ts
var InteractiveLoggerCore = class {
  constructor(options = {}) {
    this.instances = {};
    this.consoleLogging = false;
    this.singleFile = false;
    this.timestampsEnabled = true;
    this.enabled = true;
    this.consoleInterceptionEnabled = false;
    this.sessionSeparatorEnabled = true;
    this.originalConsoleMethods = {};
    var _a, _b, _c, _d, _e;
    this.storage = new StorageAdapter("__illogger__", (_a = options.maxLogs) != null ? _a : 5e3);
    this.singleFile = (_b = options.singleFile) != null ? _b : false;
    this.timestampsEnabled = (_c = options.timestamps) != null ? _c : true;
    this.enabled = (_d = options.enabled) != null ? _d : true;
    this.sessionSeparatorEnabled = (_e = options.sessionSeparator) != null ? _e : true;
    this.sessionSeparatorMessage = options.sessionSeparatorMessage;
    this.buttonOptions = options.buttonOptions;
    if (this.sessionSeparatorEnabled && this.enabled && typeof window !== "undefined" && typeof sessionStorage !== "undefined") {
      this.checkAndAddSessionSeparator();
    }
  }
  createInstance(name, options = {}) {
    var _a;
    const instance = new LoggerInstance(
      name,
      this.storage,
      (_a = options.timeStamps) != null ? _a : this.timestampsEnabled,
      this.consoleLogging,
      this.enabled
    );
    this.instances[name] = instance;
    return instance;
  }
  getLogger(name) {
    return this.instances[name];
  }
  setConsoleLogging(enabled) {
    this.consoleLogging = enabled;
    Object.values(this.instances).forEach((i) => i.setConsoleLogging(enabled));
  }
  setTimestamps(enabled) {
    this.timestampsEnabled = enabled;
    Object.values(this.instances).forEach((i) => i.setTimestamps(enabled));
  }
  getTimestamps() {
    return this.timestampsEnabled;
  }
  setEnabled(enabled) {
    this.enabled = enabled;
    Object.values(this.instances).forEach((i) => i.setEnabled(enabled));
  }
  getEnabled() {
    return this.enabled;
  }
  injectButton(buttonOptions) {
    const options = buttonOptions != null ? buttonOptions : this.buttonOptions;
    injectDownloadButton(this.storage, this.singleFile, () => this.timestampsEnabled, options);
  }
  withdrawButton() {
    withdrawDownloadButton();
  }
  async clear() {
    await this.storage.clear();
  }
  async getStats() {
    const logs = await this.storage.getAll();
    const uniqueLoggers = new Set(logs.map((log) => log == null ? void 0 : log.name).filter(Boolean));
    return {
      totalLogs: logs.length,
      activeLoggers: uniqueLoggers.size,
      maxLogs: this.storage.getMaxLogs()
    };
  }
  async setMaxLogs(maxLogs) {
    await this.storage.setMaxLogs(maxLogs);
  }
  getMaxLogs() {
    return this.storage.getMaxLogs();
  }
  async downloadLogs() {
    await downloadLogs(this.storage, this.singleFile, () => this.timestampsEnabled);
  }
  enableConsoleInterface() {
    if (typeof window !== "undefined") {
      window.downloadLogs = () => this.downloadLogs();
    }
  }
  disableConsoleInterface() {
    if (typeof window !== "undefined") {
      delete window.downloadLogs;
    }
  }
  /**
   * Enable console interception to capture all console output
   * This will intercept console.log, console.error, console.warn, console.info, console.debug, and console.trace
   */
  enableConsoleInterception() {
    if (this.consoleInterceptionEnabled) return;
    if (typeof console === "undefined") return;
    this.consoleInterceptionEnabled = true;
    this.originalConsoleMethods.log = console.log;
    this.originalConsoleMethods.error = console.error;
    this.originalConsoleMethods.warn = console.warn;
    this.originalConsoleMethods.info = console.info;
    this.originalConsoleMethods.debug = console.debug;
    this.originalConsoleMethods.trace = console.trace;
    const serializeArgs = (...args) => {
      return args.map(
        (a) => typeof a === "string" ? a : a instanceof Error ? `${a.message}
${a.stack}` : JSON.stringify(a, null, 2)
      ).join(" ");
    };
    const logToStorage = (level, ...args) => {
      const message = serializeArgs(...args);
      const entry = {
        name: "__console__",
        message: `[${level}] ${message}`,
        timestamp: this.timestampsEnabled ? (/* @__PURE__ */ new Date()).toISOString() : void 0
      };
      this.storage.append(entry).catch((error) => {
        var _a, _b;
        (_b = (_a = this.originalConsoleMethods).error) == null ? void 0 : _b.call(_a, "Failed to write console log to storage:", error);
      });
    };
    console.log = (...args) => {
      var _a;
      (_a = this.originalConsoleMethods.log) == null ? void 0 : _a.apply(console, args);
      logToStorage("LOG", ...args);
    };
    console.error = (...args) => {
      var _a;
      (_a = this.originalConsoleMethods.error) == null ? void 0 : _a.apply(console, args);
      logToStorage("ERROR", ...args);
    };
    console.warn = (...args) => {
      var _a;
      (_a = this.originalConsoleMethods.warn) == null ? void 0 : _a.apply(console, args);
      logToStorage("WARN", ...args);
    };
    console.info = (...args) => {
      var _a;
      (_a = this.originalConsoleMethods.info) == null ? void 0 : _a.apply(console, args);
      logToStorage("INFO", ...args);
    };
    console.debug = (...args) => {
      var _a;
      (_a = this.originalConsoleMethods.debug) == null ? void 0 : _a.apply(console, args);
      logToStorage("DEBUG", ...args);
    };
    console.trace = (...args) => {
      var _a;
      (_a = this.originalConsoleMethods.trace) == null ? void 0 : _a.apply(console, args);
      logToStorage("TRACE", ...args);
    };
  }
  /**
   * Disable console interception and restore original console methods
   */
  disableConsoleInterception() {
    if (!this.consoleInterceptionEnabled) return;
    if (typeof console === "undefined") return;
    this.consoleInterceptionEnabled = false;
    if (this.originalConsoleMethods.log) console.log = this.originalConsoleMethods.log;
    if (this.originalConsoleMethods.error) console.error = this.originalConsoleMethods.error;
    if (this.originalConsoleMethods.warn) console.warn = this.originalConsoleMethods.warn;
    if (this.originalConsoleMethods.info) console.info = this.originalConsoleMethods.info;
    if (this.originalConsoleMethods.debug) console.debug = this.originalConsoleMethods.debug;
    if (this.originalConsoleMethods.trace) console.trace = this.originalConsoleMethods.trace;
    this.originalConsoleMethods = {};
  }
  /**
   * Check if console interception is enabled
   */
  isConsoleInterceptionEnabled() {
    return this.consoleInterceptionEnabled;
  }
  /**
   * Check if this is a new session and add separator if needed
   * Uses sessionStorage to track if this tab has already been initialized
   */
  checkAndAddSessionSeparator() {
    const SESSION_MARKER_KEY = "__illogger_session_initialized__";
    if (sessionStorage.getItem(SESSION_MARKER_KEY)) {
      return;
    }
    try {
      sessionStorage.setItem(SESSION_MARKER_KEY, "true");
    } catch (e) {
      return;
    }
    const timestamp = this.timestampsEnabled ? (/* @__PURE__ */ new Date()).toISOString() : void 0;
    const message = this.sessionSeparatorMessage || "New Session";
    const separatorEntry = {
      name: "__session_separator__",
      message,
      timestamp,
      isSeparator: true
      // Flag to identify separator entries
    };
    this.storage.append(separatorEntry).catch((error) => {
    });
  }
};
var _illogger = null;
function InteractiveLogger(options) {
  if (!_illogger) _illogger = new InteractiveLoggerCore(options);
  return _illogger;
}
function getLogger(name) {
  return _illogger == null ? void 0 : _illogger.getLogger(name);
}
export {
  InteractiveLogger,
  getLogger
};
